import { styled, useTheme } from '@mui/material/styles';
import { Box, Container, Grid, Skeleton } from '@mui/material';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { VenueType, VenueTypeCode } from '../constants/venueTypes';
import { VenueTypeCard } from './VenueTypeCard';
import { fadeUp, staggerContainer, reducedMotionVariants } from '../../../theme/animations';
import { SectionTitle, SectionSubtitle } from './SectionHeader';
import { usePlatformStats } from '../hooks/usePlatformStats';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SportsIcon from '@mui/icons-material/Sports';
import ChildCareIcon from '@mui/icons-material/ChildCare';

const ShowcaseContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(12, 0),
  background: theme.palette.background.paper,
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(8, 0),
  },
}));

const venueTypeConfig = [
  {
    type: VenueType.GAMING,
    title: 'Gaming Venues',
    description:
      'High-performance gaming PCs, latest consoles, VR experiences and competitive esports arenas',
  },
  {
    type: VenueType.SNOOKER,
    title: 'Snooker Halls',
    description:
      'Professional-grade tables, coaching sessions, tournaments and a classic snooker experience',
  },
  {
    type: VenueType.KIDS_PLAYAREA,
    title: 'Kids Play Areas',
    description:
      'Safe, fun and engaging play zones with slides, ball pits, trampolines and creative activities',
  },
];

export function VenueTypeShowcase() {
  const navigate = useNavigate();
  const theme = useTheme();
  const { data: stats, isLoading } = usePlatformStats();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = shouldReduceMotion ? reducedMotionVariants : staggerContainer;
  const itemVariants = shouldReduceMotion ? reducedMotionVariants : fadeUp;

  const venueTypes = [
    {
      ...venueTypeConfig[0],
      icon: <SportsEsportsIcon sx={{ fontSize: 40, color: theme.palette.venue.gaming }} />,
    },
    {
      ...venueTypeConfig[1],
      icon: <SportsIcon sx={{ fontSize: 40, color: theme.palette.venue.snooker }} />,
    },
    {
      ...venueTypeConfig[2],
      icon: <ChildCareIcon sx={{ fontSize: 40, color: theme.palette.venue.kids }} />,
    },
  ];

  const handleCardClick = (venueType: VenueTypeCode) => {
    navigate(`/venues?type=${venueType}`);
  };

  return (
    <ShowcaseContainer>
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <motion.div variants={itemVariants}>
            <SectionTitle>Explore Venue Categories</SectionTitle>
          </motion.div>

          <motion.div variants={itemVariants}>
            <SectionSubtitle>
              From gaming arenas to family fun zones, find the perfect venue for every occasion
            </SectionSubtitle>
          </motion.div>

          <Grid container spacing={4}>
            {venueTypes.map((venue, index) => (
              <Grid size={{ xs: 12, md: 4 }} key={venue.type}>
                {isLoading ? (
                  <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
                ) : (
                  <motion.div
                    variants={itemVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <VenueTypeCard
                      venueType={venue.type}
                      title={venue.title}
                      description={venue.description}
                      icon={venue.icon}
                      venueCount={stats?.venueTypeCounts?.[venue.type]}
                      onClick={() => handleCardClick(venue.type)}
                    />
                  </motion.div>
                )}
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </ShowcaseContainer>
  );
}
